import { CopyOutlined, LinkOutlined, LoadingOutlined, LogoutOutlined, ThunderboltOutlined, UserOutlined } from '@ant-design/icons';
import { history, useModel } from '@umijs/max';
import { Avatar, Button, Dropdown, Input, message, Modal, Space, theme } from 'antd';
import type { MenuProps } from 'antd';
import React, { useState } from 'react';
import { flushSync } from 'react-dom';
import { getAffCode, outLogin } from '@/services/auth';
import { loginModal } from '@/components/LoginModal';

const SiderFooter: React.FC<{ collapsed?: boolean }> = ({ collapsed }) => {
  const { token } = theme.useToken();
  const { initialState, setInitialState } = useModel('@@initialState');
  const [inviteModalOpen, setInviteModalOpen] = useState(false);
  const [inviteLink, setInviteLink] = useState('');
  const [loading, setLoading] = useState(false);

  const isLoggedIn = !!initialState?.currentUser;

  // 退出登录
  const loginOut = async () => {
    localStorage.removeItem('token');
    await outLogin();
    message.success('退出登录成功');
    history.replace('/home');
  };

  // 复制到剪贴板
  const copyToClipboard = async (text: string) => {
    try {
      // 优先使用现代 Clipboard API
      if (navigator.clipboard && window.isSecureContext) {
        await navigator.clipboard.writeText(text);
        message.success('邀请链接已复制到剪贴板');
        return;
      }
      // fallback: 使用传统方式
      const textarea = document.createElement('textarea');
      textarea.value = text;
      textarea.style.position = 'fixed';
      textarea.style.left = '-9999px';
      document.body.appendChild(textarea);
      textarea.focus();
      textarea.select();
      const success = document.execCommand('copy');
      document.body.removeChild(textarea);
      if (success) {
        message.success('邀请链接已复制到剪贴板');
      } else {
        message.error('复制失败，请手动复制');
      }
    } catch (err) {
      message.error('复制失败，请手动复制');
    }
  };

  // 获取邀请链接
  const handleGetInviteLink = async () => {
    setLoading(true);
    try {
      const res = await getAffCode();
      if (res.data) {
        setInviteLink(res.data);
        setInviteModalOpen(true);
        // 弹窗显示时自动复制
        copyToClipboard(res.data);
      } else {
        message.error(res.message || '获取邀请链接失败');
      }
    } catch {
      // 全局错误处理会统一处理错误提示和 401 登录弹窗
    } finally {
      setLoading(false);
    }
  };

  // 点击复制按钮
  const handleCopyLink = () => {
    copyToClipboard(inviteLink);
  };

  const onMenuClick: MenuProps['onClick'] = (event) => {
    const { key } = event;
    if (key === 'logout') {
      flushSync(() => {
        setInitialState((s) => ({ ...s, currentUser: undefined }));
      });
      loginOut();
    }
  };

  const menuItems: MenuProps['items'] = [
    {
      key: 'logout',
      icon: <LogoutOutlined />,
      label: '退出登录',
    },
  ];

  const userDropdown = (
    <Dropdown
      menu={{ items: menuItems, onClick: onMenuClick }}
      placement="topRight"
      trigger={['click']}
    >
      <div
        style={{
          display: 'flex',
          alignItems: 'center',
          gap: 8,
          padding: collapsed ? '8px' : '6px 8px',
          cursor: 'pointer',
          borderRadius: token.borderRadius,
          transition: 'background-color 0.2s',
        }}
        onMouseEnter={(e) => {
          e.currentTarget.style.backgroundColor = token.colorBgTextHover;
        }}
        onMouseLeave={(e) => {
          e.currentTarget.style.backgroundColor = 'transparent';
        }}
      >
        <Avatar
          size={28}
          icon={<UserOutlined />}
          src={initialState?.currentUser?.user?.avatar}
          style={{ backgroundColor: token.colorPrimary }}
        />
        {!collapsed && (
          <span style={{ fontSize: 14, fontWeight: 500 }}>
            {initialState?.currentUser?.user?.name || '未登录'}
          </span>
        )}
      </div>
    </Dropdown>
  );

  if (collapsed) {
    return (
      <div
        style={{
          padding: '12px 8px',
          borderTop: `1px solid ${token.colorBorderSecondary}`,
        }}
      >
        <Space direction="vertical" style={{ width: '100%' }} align="center">
          {isLoggedIn ? (
            userDropdown
          ) : (
            <div
              style={{
                padding: '8px',
                cursor: 'pointer',
                borderRadius: token.borderRadius,
                transition: 'background-color 0.2s',
              }}
              onClick={() => loginModal.show()}
              onMouseEnter={(e) => {
                e.currentTarget.style.backgroundColor = token.colorBgTextHover;
              }}
              onMouseLeave={(e) => {
                e.currentTarget.style.backgroundColor = 'transparent';
              }}
            >
              <Avatar
                size={28}
                icon={<UserOutlined />}
                style={{ backgroundColor: token.colorPrimary }}
              />
            </div>
          )}
        </Space>
      </div>
    );
  }

  // 所有行左侧图标统一宽度，与 Avatar(28px) 等宽，小图标居中
  const ICON_COL_SIZE = 28;

  const iconSlot: React.CSSProperties = {
    width: ICON_COL_SIZE,
    height: ICON_COL_SIZE,
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    flexShrink: 0,
    fontSize: 16,
  };

  const rowStyle: React.CSSProperties = {
    display: 'flex',
    alignItems: 'center',
    gap: 8,
    padding: '6px 8px',
    cursor: 'pointer',
    borderRadius: token.borderRadius,
    transition: 'background-color 0.2s',
  };

  const handleRowHoverEnter = (e: React.MouseEvent<HTMLDivElement>) => {
    e.currentTarget.style.backgroundColor = token.colorBgTextHover;
  };
  const handleRowHoverLeave = (e: React.MouseEvent<HTMLDivElement>) => {
    e.currentTarget.style.backgroundColor = 'transparent';
  };

  return (
    <div
      style={{
        padding: '8px 4px',
        borderTop: `1px solid ${token.colorBorderSecondary}`,
        display: 'flex',
        flexDirection: 'column',
        gap: 2,
      }}
    >
      {/* 积分 + 升级 */}
      <div
        style={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          padding: '6px 8px',
          borderRadius: token.borderRadius,
        }}
      >
        <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
          <span style={{ ...iconSlot, color: token.colorPrimary }}>
            <ThunderboltOutlined />
          </span>
          <span style={{ fontSize: 14, fontWeight: 500 }}>1088</span>
        </div>
        <Button
          type="primary"
          size="small"
          style={{
            borderRadius: 16,
            fontSize: 12,
            height: 26,
            padding: '0 12px',
          }}
        >
          充值
        </Button>
      </div>

      {/*{isLoggedIn && (*/}
      {/*  <div*/}
      {/*    style={{ ...rowStyle, color: token.colorTextSecondary }}*/}
      {/*    onClick={handleGetInviteLink}*/}
      {/*    onMouseEnter={handleRowHoverEnter}*/}
      {/*    onMouseLeave={handleRowHoverLeave}*/}
      {/*  >*/}
      {/*    <span style={iconSlot}>*/}
      {/*      {loading ? <LoadingOutlined /> : <LinkOutlined />}*/}
      {/*    </span>*/}
      {/*    <span style={{ fontSize: 14 }}>邀请链接</span>*/}
      {/*  </div>*/}
      {/*)}*/}

      {isLoggedIn ? (
        // 已登录：显示用户信息和退出登录
        userDropdown
      ) : (
        // 未登录：显示登录按钮
        <div
          style={{ ...rowStyle, color: token.colorTextSecondary }}
          onClick={() => loginModal.show()}
          onMouseEnter={handleRowHoverEnter}
          onMouseLeave={handleRowHoverLeave}
        >
          <span style={iconSlot}>
            <UserOutlined />
          </span>
          <span style={{ fontSize: 14 }}>登录</span>
        </div>
      )}

      {isLoggedIn && (
        <Modal
          title="邀请链接"
          open={inviteModalOpen}
          onCancel={() => setInviteModalOpen(false)}
          footer={null}
          width={480}
        >
          <div style={{ marginTop: 16 }}>
            <p style={{ marginBottom: 8, color: token.colorTextSecondary }}>
              分享以下链接邀请好友注册：
            </p>
            <Input.Search
              value={inviteLink}
              readOnly
              enterButton={
                <Button type="primary" icon={<CopyOutlined />} onClick={handleCopyLink}>
                  复制
                </Button>
              }
            />
          </div>
        </Modal>
      )}
    </div>
  );
};

export default SiderFooter;
